import {CircularProgress} from '@logistics-frontend/blocks/legacy'
import {Alert} from '@logistics-frontend/blocks/lpc/alert'
import {Button} from '@logistics-frontend/blocks/lpc/button'
import {useHistory} from '@logistics-frontend/blocks/lpc/react-router'
import {useI18n} from '@logistics-frontend/core/i18n'
import {firstOf, get} from '@logistics-frontend/utils'
import {Snackbar} from '@material-ui/core'
import {AsyncStatus, useSubmitRequest, useTariff} from '@yandex-taxi/react-integration-api'
import React, {useCallback, useEffect, useMemo, useState} from 'react'

import {CLASS_FOR_SCROLL_TO_ERROR} from '../../consts'
import {useInvalidOrderGoalCallback, usePassportGoalCallback, useSuccessOrderGoalCallback} from '../metrika'
import {position} from './consts'
import {useEstimateTitle} from './hooks'
import {ErrorCode} from './types'

type DataError = {
  data?: {
    code?: string
    error?: {
      code?: string
    }
  }
}

const getSubmitError = firstOf<DataError, string>(get('data.code'), get('data.error.code'))

const OrderButton: React.FC = () => {
  const t = useI18n()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [scrollFlag, setScrollFlag] = useState<boolean>(true)
  const [createHandler, {status, data, error}] = useSubmitRequest()
  const {replace} = useHistory()
  const [{value: tariff}] = useTariff()
  const startOrderGoal = usePassportGoalCallback('SendOrder')
  const successOrderGoal = useSuccessOrderGoalCallback()
  const invalidOrderGoal = useInvalidOrderGoalCallback()
  const clickHandler = useCallback(() => {
    // создадим заказ
    createHandler()
    // очистим хранилище
    setScrollFlag(!scrollFlag)
  }, [createHandler, scrollFlag])
  const children = useEstimateTitle(t('order.form.order_button_text', 'Заказать'), tariff as string, true)
  const loading = status === AsyncStatus.PENDING
  const startIcon = loading ? <CircularProgress color="secondary" size={20} /> : undefined
  const id: string | undefined = data?.orderid

  const DEFAULT_ORDER_SUBMIT_ERROR_MESSAGE = t('order.form.order_formation_error', 'Ошибка формирования заказа')
  const errorCodeToMessage: Record<string, string> = useMemo(
    () => ({
      [ErrorCode.PAYMENT_TYPE_UNACCEPTABLE]: t(
        'order.form.payment_method_not_supported_error',
        'Данный способ оплаты не поддерживается',
      ),
      [ErrorCode.INVALID_PHONE_NUMBER]: t('order.form.invalid_phone_number_error', 'Неверный формат номера телефона'),
    }),
    [t],
  )

  useEffect(() => {
    const input = document.body.querySelector(`.${CLASS_FOR_SCROLL_TO_ERROR}`)

    if (input) {
      input.scrollIntoView({block: 'start', behavior: 'smooth'})
      invalidOrderGoal()
    }
  }, [invalidOrderGoal, scrollFlag])

  useEffect(() => {
    if (status === AsyncStatus.SUCCESS) {
      successOrderGoal()
      replace(id ? `orders/${id}` : 'orders')
    }
  }, [id, replace, status, successOrderGoal])

  useEffect(() => {
    if (status === AsyncStatus.PENDING) {
      startOrderGoal()
    }
  }, [startOrderGoal, status])

  useEffect(() => {
    if (status === AsyncStatus.ERROR) {
      const code = getSubmitError(error as unknown as DataError)
      const errorMessage = (code && errorCodeToMessage[code]) || DEFAULT_ORDER_SUBMIT_ERROR_MESSAGE
      setErrorMessage(errorMessage)
    }
  }, [setErrorMessage, error, status, DEFAULT_ORDER_SUBMIT_ERROR_MESSAGE, errorCodeToMessage])

  return (
    <>
      <Button
        children={children}
        color="primary"
        disabled={loading}
        onClick={clickHandler}
        startIcon={startIcon}
        variant="contained"
        fullWidth
      />
      <Snackbar
        anchorOrigin={position}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
        open={!!errorMessage}
      >
        <Alert severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default OrderButton
